import { usePage } from "@inertiajs/react"
import { BrandMotif } from "@/components/ui/brand-motif"
import type { PageProps, PublicTestimonial } from "@/types/inertia"

/**
 * "What clients say" — the review band from Gisela's home page, shared so the
 * blog can end on the same social proof rather than a second implementation
 * that drifts from it. Callers decide whether to show it (the
 * `testimonials_display` feature) and what it sits on: the band paints its own
 * background, and two cream bands in a row have no seam between them.
 *
 * With nothing published it shows a placeholder quote rather than an empty
 * band, so a page laid out around it does not collapse before the first real
 * testimonial arrives.
 */
export function TestimonialsBand({
  testimonials,
  className = "bg-cream",
}: {
  testimonials: PublicTestimonial[]
  className?: string
}) {
  const t = usePage<PageProps>().props.translations.gisela_pages.profile

  return (
    <section className={`relative overflow-hidden py-16 lg:py-20 ${className}`}>
      <BrandMotif
        variant="speckle"
        tone="navy-light"
        opacity={0.8}
        seed={7}
        className="absolute -bottom-10 right-16"
      />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <h2 className="text-center">{t.testimonials_heading}</h2>

        {testimonials.length > 0 ? (
          <div
            className={
              testimonials.length === 1
                ? "mx-auto mt-10 max-w-3xl"
                : "mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
            }
          >
            {testimonials.map((quote) => (
              <div key={quote.id} className="rounded-2xl bg-white p-6 ring-1 ring-hairline">
                <QuoteMark />
                <blockquote>
                  <p className="whitespace-pre-line text-ink-body" style={{ fontStyle: "italic" }}>
                    {quote.quote}
                  </p>
                  <footer className="mt-4">
                    <cite className="text-sm text-ink-muted not-italic">{quote.attribution}</cite>
                  </footer>
                </blockquote>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-10 rounded-2xl bg-white p-8 text-center ring-1 ring-hairline">
            <div className="flex justify-center">
              <QuoteMark />
            </div>
            <p className="text-ink-muted" style={{ fontStyle: "italic" }}>
              "{t.testimonial_placeholder}"
            </p>
            <p className="mt-3 text-sm text-ink-muted">— {t.testimonials_coming_soon}</p>
          </div>
        )}
      </div>
    </section>
  )
}

export function QuoteMark() {
  return (
    <svg className="mb-4 h-6 w-6 text-sage" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4.583 17.321C3.553 16.227 3 15 3 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179zm10 0C13.553 16.227 13 15 13 13.011c0-3.5 2.457-6.637 6.03-8.188l.893 1.378c-3.335 1.804-3.987 4.145-4.247 5.621.537-.278 1.24-.375 1.929-.311 1.804.167 3.226 1.648 3.226 3.489a3.5 3.5 0 01-3.5 3.5c-1.073 0-2.099-.49-2.748-1.179z" />
    </svg>
  )
}
